import type {
  BandwidthActivityAllocationMetrics,
  BandwidthActivityAllocationRow,
  BandwidthAllocationMetrics,
  BandwidthAllocationTeamRosterRow,
  BandwidthCapacityDeductionCell,
  BandwidthDashboardData,
  BandwidthGroupBreakdownRow,
  BandwidthGroupUserCustomerCell,
  BandwidthTeamResourceAllocationRow,
  BandwidthTypeByCustomerCell,
} from './bandwidthDashboardTypes';
import {
  buildCapacityDeductionLookups,
  getCapacityDeductionHoursForGroupUser,
  getEffectiveBandwidthCapacityHours,
  isBandwidthCapacityDeductionType,
} from './bandwidthCapacityDeductionTypes';
import { getBandwidthCapacityHoursForWorkLocation, getBandwidthUtilizationPct } from './bandwidthUtilization';
import { bwExportPercent, bwExportPercentPdf, roundBwDecimal } from './bandwidthFormat';

function roundHours(n: number): number {
  return roundBwDecimal(n);
}

export function computeBandwidthAllocationMetrics(
  billableHours: number,
  nonBillableHours: number,
  capacityHours: number
): BandwidthAllocationMetrics {
  const loggedHours = billableHours + nonBillableHours;
  const totalHours = Math.max(0, capacityHours);
  const availableHours = roundHours(Math.max(0, totalHours - loggedHours));
  return {
    totalHours: roundHours(totalHours),
    billableHours: roundHours(billableHours),
    nonBillableHours: roundHours(nonBillableHours),
    availableHours,
    bandwidthUtilizedPct: getBandwidthUtilizationPct(loggedHours, totalHours),
    bandwidthAvailablePct: getBandwidthUtilizationPct(availableHours, totalHours),
  };
}

function billableHoursFromCells(
  cells: BandwidthGroupUserCustomerCell[]
): { billableHours: number; nonBillableHours: number } {
  let billableMinutes = 0;
  let nonBillableMinutes = 0;
  for (const cell of cells) {
    if (cell.billableMinutes != null || cell.nonBillableMinutes != null) {
      billableMinutes += cell.billableMinutes ?? 0;
      nonBillableMinutes += cell.nonBillableMinutes ?? 0;
      continue;
    }
    billableMinutes += cell.minutes;
  }
  return {
    billableHours: billableMinutes / 60,
    nonBillableHours: nonBillableMinutes / 60,
  };
}

export function buildTeamResourceAllocationRows(
  groups: BandwidthGroupBreakdownRow[],
  cells: BandwidthGroupUserCustomerCell[],
  dateFrom: string,
  dateTo: string,
  allocationRosters: BandwidthAllocationTeamRosterRow[] = [],
  capacityDeductions: BandwidthCapacityDeductionCell[] = []
): BandwidthTeamResourceAllocationRow[] {
  const deductionLookups = buildCapacityDeductionLookups(capacityDeductions);
  const rosterByGroup = new Map(allocationRosters.map((r) => [r.groupId, r.users]));
  const cellsByGroupUser = new Map<string, BandwidthGroupUserCustomerCell[]>();
  for (const cell of cells) {
    const key = `${cell.groupId}\t${cell.userId}`;
    const list = cellsByGroupUser.get(key) ?? [];
    list.push(cell);
    cellsByGroupUser.set(key, list);
  }

  const rows: BandwidthTeamResourceAllocationRow[] = [];

  for (const group of groups) {
    const capPerPerson = getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo, group.workLocation);
    const userIdSet = new Set<string>();
    const labelByUserId = new Map<string, string>();

    for (const user of group.users) {
      userIdSet.add(user.userId);
      labelByUserId.set(user.userId, user.userName);
    }
    for (const rosterUser of rosterByGroup.get(group.groupId) ?? []) {
      userIdSet.add(rosterUser.userId);
      if (!labelByUserId.has(rosterUser.userId)) {
        labelByUserId.set(rosterUser.userId, rosterUser.userName);
      }
    }
    for (const cell of cells) {
      if (cell.groupId !== group.groupId) continue;
      userIdSet.add(cell.userId);
    }

    if (userIdSet.size === 0) continue;

    let teamBillable = 0;
    let teamNonBillable = 0;

    const resources: BandwidthTeamResourceAllocationRow[] = [...userIdSet]
      .map((userId) => {
        const userCells = cellsByGroupUser.get(`${group.groupId}\t${userId}`) ?? [];
        const { billableHours, nonBillableHours } = billableHoursFromCells(userCells);
        teamBillable += billableHours;
        teamNonBillable += nonBillableHours;
        const deductionHours = getCapacityDeductionHoursForGroupUser(
          deductionLookups,
          group.groupId,
          userId
        );
        const effectiveCapacity = getEffectiveBandwidthCapacityHours(capPerPerson, deductionHours);
        return {
          id: userId,
          label: labelByUserId.get(userId) ?? userId,
          kind: 'resource' as const,
          ...computeBandwidthAllocationMetrics(billableHours, nonBillableHours, effectiveCapacity),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    let teamCapacity = 0;
    for (const resource of resources) {
      teamCapacity += resource.totalHours;
    }

    rows.push({
      id: group.groupId,
      label: group.groupName,
      kind: 'team' as const,
      resources,
      ...computeBandwidthAllocationMetrics(teamBillable, teamNonBillable, teamCapacity),
    });
  }

  return rows;
}

export function buildActivityAllocationRows(
  typeByCustomer: BandwidthTypeByCustomerCell[]
): BandwidthActivityAllocationRow[] {
  const byType = new Map<
    string,
    {
      activityTypeLabel: string;
      billableMinutes: number;
      nonBillableMinutes: number;
      displayMinutes: number;
    }
  >();

  for (const cell of typeByCustomer) {
    const prev = byType.get(cell.activityType) ?? {
      activityTypeLabel: cell.activityTypeLabel,
      billableMinutes: 0,
      nonBillableMinutes: 0,
      displayMinutes: 0,
    };
    const isDeduction = cell.reducesCapacity === true || isBandwidthCapacityDeductionType(cell.activityType);
    if (isDeduction) {
      prev.displayMinutes += cell.totalMinutes;
    } else if (cell.isBillable === false) {
      prev.nonBillableMinutes += cell.totalMinutes;
    } else {
      prev.billableMinutes += cell.totalMinutes;
    }
    byType.set(cell.activityType, prev);
  }

  return [...byType.entries()]
    .map(([activityType, agg]) => {
      const isDeduction = isBandwidthCapacityDeductionType(activityType);
      const billableHours = isDeduction ? 0 : agg.billableMinutes / 60;
      const nonBillableHours = isDeduction ? 0 : agg.nonBillableMinutes / 60;
      const totalHours = isDeduction ? agg.displayMinutes / 60 : billableHours + nonBillableHours;
      return {
        activityType,
        activityTypeLabel: agg.activityTypeLabel,
        ...computeActivityAllocationMetrics(billableHours, nonBillableHours, totalHours),
      };
    })
    .filter((r) => r.totalHours > 0)
    .sort((a, b) => b.totalHours - a.totalHours);
}

export function computeActivityAllocationMetrics(
  billableHours: number,
  nonBillableHours: number,
  totalHoursOverride?: number
): BandwidthActivityAllocationMetrics {
  const totalHours =
    totalHoursOverride != null ? roundHours(totalHoursOverride) : roundHours(billableHours + nonBillableHours);
  return {
    totalHours,
    billableHours: roundHours(billableHours),
    nonBillableHours: roundHours(nonBillableHours),
    billablePct: getBandwidthUtilizationPct(billableHours, totalHours),
    nonBillablePct: getBandwidthUtilizationPct(nonBillableHours, totalHours),
  };
}

export function computeActivityAllocationFooter(
  rows: BandwidthActivityAllocationRow[]
): BandwidthActivityAllocationMetrics {
  let billableHours = 0;
  let nonBillableHours = 0;
  for (const row of rows) {
    if (isBandwidthCapacityDeductionType(row.activityType)) continue;
    billableHours += row.billableHours;
    nonBillableHours += row.nonBillableHours;
  }
  return computeActivityAllocationMetrics(billableHours, nonBillableHours);
}

export function computeScopeCapacityHours(
  groups: BandwidthGroupBreakdownRow[],
  dateFrom: string,
  dateTo: string,
  allocationRosters: BandwidthAllocationTeamRosterRow[] = [],
  capacityDeductions: BandwidthCapacityDeductionCell[] = []
): number {
  const groupById = new Map(groups.map((g) => [g.groupId, g]));
  const deductionLookups = buildCapacityDeductionLookups(capacityDeductions);
  const seenUsers = new Set<string>();
  let totalCapacity = 0;

  for (const roster of allocationRosters) {
    const group = groupById.get(roster.groupId);
    const capPerPerson = getBandwidthCapacityHoursForWorkLocation(
      dateFrom,
      dateTo,
      group?.workLocation
    );
    for (const user of roster.users) {
      if (seenUsers.has(user.userId)) continue;
      seenUsers.add(user.userId);
      const deductionHours = deductionLookups.byUser.get(user.userId) ?? 0;
      totalCapacity += getEffectiveBandwidthCapacityHours(capPerPerson, deductionHours);
    }
  }

  for (const group of groups) {
    const capPerPerson = getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo, group.workLocation);
    for (const user of group.users) {
      if (seenUsers.has(user.userId)) continue;
      seenUsers.add(user.userId);
      const deductionHours = deductionLookups.byUser.get(user.userId) ?? 0;
      totalCapacity += getEffectiveBandwidthCapacityHours(capPerPerson, deductionHours);
    }
  }

  if (seenUsers.size === 0) {
    return roundHours(getBandwidthCapacityHoursForWorkLocation(dateFrom, dateTo));
  }

  return roundHours(totalCapacity);
}

export function buildAllocationViewsFromDashboard(
  data: BandwidthDashboardData,
  dateFrom: string,
  dateTo: string
): {
  teamResourceRows: BandwidthTeamResourceAllocationRow[];
  activityRows: BandwidthActivityAllocationRow[];
  scopeCapacityHours: number;
} {
  const groups = data.groupUserBreakdown ?? [];
  const cells = data.groupUserCustomerCells ?? [];
  const allocationRosters = data.allocationTeamRosters ?? [];
  const capacityDeductions = data.capacityDeductionByGroupUser ?? [];
  const scopeCapacityHours = computeScopeCapacityHours(
    groups,
    dateFrom,
    dateTo,
    allocationRosters,
    capacityDeductions
  );

  return {
    teamResourceRows: buildTeamResourceAllocationRows(
      groups,
      cells,
      dateFrom,
      dateTo,
      allocationRosters,
      capacityDeductions
    ),
    activityRows: buildActivityAllocationRows(data.typeByCustomer ?? []),
    scopeCapacityHours,
  };
}

export function computeAllocationFooter(
  rows: BandwidthAllocationMetrics[],
  capacityHours: number
): BandwidthAllocationMetrics {
  let billableHours = 0;
  let nonBillableHours = 0;
  for (const row of rows) {
    billableHours += row.billableHours;
    nonBillableHours += row.nonBillableHours;
  }
  return computeBandwidthAllocationMetrics(billableHours, nonBillableHours, capacityHours);
}

export type AllocationSortKey =
  | 'label'
  | 'totalHours'
  | 'billableHours'
  | 'nonBillableHours'
  | 'availableHours'
  | 'bandwidthUtilizedPct'
  | 'bandwidthAvailablePct'
  | 'billablePct'
  | 'nonBillablePct';

export type AllocationSortDir = 'asc' | 'desc';

function allocationMetricValue(
  row: BandwidthAllocationMetrics | BandwidthActivityAllocationMetrics,
  key: AllocationSortKey
): number | string {
  if (key === 'label') return '';
  if (key === 'billablePct' || key === 'nonBillablePct') {
    return (row as BandwidthActivityAllocationMetrics)[key] ?? -1;
  }
  if (key === 'bandwidthUtilizedPct' || key === 'bandwidthAvailablePct') {
    return (row as BandwidthAllocationMetrics)[key] ?? -1;
  }
  if (key === 'availableHours') {
    return (row as BandwidthAllocationMetrics).availableHours ?? -1;
  }
  return row[key as 'totalHours' | 'billableHours' | 'nonBillableHours'] ?? -1;
}

export function compareAllocationBySortKey<
  T extends (BandwidthAllocationMetrics | BandwidthActivityAllocationMetrics) & { label: string },
>(
  a: T,
  b: T,
  sortKey: AllocationSortKey,
  sortDir: AllocationSortDir
): number {
  const dirMul = sortDir === 'desc' ? -1 : 1;
  if (sortKey === 'label') {
    return dirMul * a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  }
  const av = allocationMetricValue(a, sortKey);
  const bv = allocationMetricValue(b, sortKey);
  if (typeof av === 'number' && typeof bv === 'number') {
    return dirMul * (av - bv);
  }
  return 0;
}

export function sortTeamResourceAllocationRows(
  rows: BandwidthTeamResourceAllocationRow[],
  sortKey: AllocationSortKey,
  sortDir: AllocationSortDir
): BandwidthTeamResourceAllocationRow[] {
  const sortedTeams = [...rows].sort((a, b) => compareAllocationBySortKey(a, b, sortKey, sortDir));
  if (sortKey === 'label') {
    return sortedTeams.map((team) => ({
      ...team,
      resources: [...(team.resources ?? [])].sort((a, b) =>
        compareAllocationBySortKey(a, b, sortKey, sortDir)
      ),
    }));
  }
  return sortedTeams.map((team) => ({
    ...team,
    resources: [...(team.resources ?? [])].sort((a, b) => compareAllocationBySortKey(a, b, sortKey, sortDir)),
  }));
}

export function sortActivityAllocationRows(
  rows: BandwidthActivityAllocationRow[],
  sortKey: AllocationSortKey,
  sortDir: AllocationSortDir
): BandwidthActivityAllocationRow[] {
  return [...rows].sort((a, b) =>
    compareAllocationBySortKey(
      { ...a, label: a.activityTypeLabel },
      { ...b, label: b.activityTypeLabel },
      sortKey,
      sortDir
    )
  );
}

export function allocationExportPct(value: number | null): string | number {
  return bwExportPercent(value);
}

export function allocationExportPctPdf(value: number | null): string {
  return bwExportPercentPdf(value);
}

export function sumAllocationMetrics(rows: BandwidthAllocationMetrics[]): BandwidthAllocationMetrics {
  let billableHours = 0;
  let nonBillableHours = 0;
  let totalHours = 0;
  for (const row of rows) {
    billableHours += row.billableHours;
    nonBillableHours += row.nonBillableHours;
    totalHours += row.totalHours;
  }
  return computeBandwidthAllocationMetrics(billableHours, nonBillableHours, totalHours);
}
